import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuthToken, getUser, logout } from '../services/auth';
import { getCurrentUser } from '../services/api';

export default function AuthChecker({ children }) {
  const [isChecking, setIsChecking] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const redirectingRef = useRef(false);

  useEffect(() => {
    const checkAuth = async () => {
      const token = getAuthToken();
      const user = getUser();
      
      // Public routes that don't need auth (javni profil dadilje: /babysitter/:id)
      const publicRoutes = ['/', '/login', '/register', '/email-login', '/terms', '/privacy'];
      const isPublicRoute =
        publicRoutes.includes(location.pathname) ||
        location.pathname.startsWith('/babysitter/') ||
        location.pathname.startsWith('/job/');

      if (!token || !user) {
        if (!isPublicRoute) {
          navigate('/login');
        }
        setIsChecking(false);
        return;
      }

      // If user is authenticated but visiting auth pages, redirect to dashboard
      const requiresProfileCompletion = user?.profileComplete === false;
      const profileCompletionRoute = '/complete-profile';

      const redirectIfAuthenticated = ['/login', '/register', '/email-login'];
      if (!redirectingRef.current && redirectIfAuthenticated.includes(location.pathname)) {
        redirectingRef.current = true;
        navigate(requiresProfileCompletion ? profileCompletionRoute : '/dashboard', { replace: true });
        setIsChecking(false);
        return;
      } else if (!redirectIfAuthenticated.includes(location.pathname)) {
        redirectingRef.current = false;
      }

      if (requiresProfileCompletion && location.pathname !== profileCompletionRoute) {
        navigate(profileCompletionRoute, { replace: true });
        setIsChecking(false);
        return;
      }

      // For protected routes, verify token is still valid
      if (!isPublicRoute) {
        try {
          const result = await getCurrentUser();
          if (!result.success) {
            // Token invalid, clear and redirect
            logout();
            navigate('/login');
          }
        } catch (error) {
          console.error('Auth check error:', error);
          // If API call fails, still allow access if token exists
          // User might be offline or API temporarily unavailable
        }
      }

      setIsChecking(false);
    };

    checkAuth();
  }, [location.pathname, navigate]);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Učitavanje...</p>
        </div>
      </div>
    );
  }

  return children;
}

